import type { Dispatch } from '@reduxjs/toolkit';
import Cookies from 'cookies-js';
import { formatISO } from 'date-fns';
import type { SubscriptionSearch } from '../@types/AdvertismentSubscription';
import type { ElasticSearchResponse } from '../@types/ElasticSearchResponse';
import type { ElasticRetailerInfo, ElasticVehicle } from '../@types/ElasticVehicle';
import type { InsuranceProviderResponse } from '../@types/Insurance';
import type { Retailer } from '../@types/Retailer';
import type { Vehicle } from '../@types/Vehicle';
import type { Assignment } from '../@types/assignment';
import type { DetailedVehicle } from '../@types/detailed-vehicle';
import type Item from '../@types/item';
import type { RetailerInfo } from '../@types/item';
import type { PropertyTab } from '../@types/tracking';
import type { RootState } from '../reducers';
import EventContext from '../shared/event-context';
import {
  ElectricCostEvents,
  assignmentEvents,
  conversionEvents,
  eventTypes,
  interactionEventType,
  otherEvents,
  softConversionEvents,
} from '../shared/tracking';
import { trackWithUser, trackerHub, trackerHubInstant } from '../utils';
import { isString } from '../utils/string';
import { COOKIE_CENTRAL_STORAGE } from './retailer';

type SoftConversionType = {
  id: string | null;
  tracked: string[];
  shareTracked: string[];
};

const source = 'web';
const __current_search: { [key: string]: number | undefined } = {};
const __softConversion: SoftConversionType = {
  id: null,
  tracked: [],
  shareTracked: [],
};

const getSearchSortOption = (response: ElasticSearchResponse<ElasticVehicle>) => {
  let sortOption = null;
  if (response.documentList && response.documentList.sortOptions) {
    sortOption = response.documentList.sortOptions
      .filter((so) => so.selected)
      .map((so) => so.query)[0];
  }
  return sortOption;
};

const createUrlParams = (params: string) => (params ? { urlParameters: params } : {});

const createConversionType = (conversionType: string | null | undefined) =>
  isString(conversionType) ? { conversionType } : {};

const createSearchTimingObject = (params: string) =>
  (__current_search[JSON.stringify(params)] = new Date().getTime());

const createSearchTimingValue = (params: string) => {
  const v = __current_search[JSON.stringify(params)];

  return v ? new Date().getTime() - v : -1;
};

const createSearchStats = (_params: string, response: ElasticSearchResponse<ElasticVehicle>) =>
  response.stats ? response.stats : {};

export const createSearchData = (
  params: string,
  response: ElasticSearchResponse<ElasticVehicle>,
  newsItemSearchMatchedId: string | null = null,
) =>
  response
    ? {
        ...createSearchStats(params, response),
        searchRequestTimeInMillis: createSearchTimingValue(params),
        sortOption: getSearchSortOption(response),
        newsItemSearchMatchedId,
      }
    : {};

interface MapCentralStorageBranch {
  id: string | undefined;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  name: string | null;
  connectedBranches: never[];
  isCentralStorage: boolean;
}

const mapCentralStorageBranch = (
  item: DetailedVehicle | ElasticVehicle | Item,
): MapCentralStorageBranch | null => {
  const id = Cookies.get(COOKIE_CENTRAL_STORAGE);

  const branch =
    id &&
    (item as ElasticVehicle | Item)?.branches?.[0]?.connectedBranches?.find((x) => x.id === id);

  if (branch) {
    return {
      id: branch?.id,
      city: branch?.position?.city ? branch.position.city : null,
      latitude: branch?.position?.location?.lat ? branch.position.location.lat : null,
      longitude: branch?.position?.location?.lon ? branch.position.location.lon : null,
      name: branch?.name || null,
      connectedBranches: [],
      isCentralStorage: false,
    };
  }
  return null;
};

const mapLeasingDetails = (
  branch: MapCentralStorageBranch | ElasticRetailerInfo | RetailerInfo | { name: string } | null,
  item: ElasticVehicle | DetailedVehicle | Item,
) => {
  const leasingPromos = (item as ElasticVehicle | Item).leasingPromos?.[0];
  return leasingPromos
    ? {
        leasing: true,
        'service-agreement': leasingPromos.serviceIncluded,
        'dealer-car-insurance': leasingPromos.insuranceIncluded,
        'other-services': leasingPromos.description,
        ...branch,
      }
    : branch;
};

export const mapVehicleData = (
  item: ElasticVehicle | DetailedVehicle | Item | null | undefined,
  overrideBranch = false,
) => {
  if (!item) {
    return null;
  }

  let branches: string[] | null = null;
  let branchDetails: any[] | null = null;

  if (overrideBranch && mapCentralStorageBranch(item)) {
    const branch = mapCentralStorageBranch(item);
    branches = branch?.id ? [branch.id] : [];
    branchDetails = [mapLeasingDetails(branch, item)];
  } else {
    branches = ((item as ElasticVehicle | Item)?.branches || []).map((b) => b.id);
    branchDetails = (
      (item as ElasticVehicle | Item)?.branches || [
        {
          name: 'private',
        },
      ]
    ).map((b) => mapLeasingDetails(b, item));
  }

  return {
    id: (item as Item)._id || (item as ElasticVehicle | Item)?.id,
    manufacturer: item.manufacturer,
    model: item.modelSeries,
    price: (item as ElasticVehicle | Item)?.price,
    manufactureYear: item.manufactureYear,
    fuelType: item.fuelType,
    mileage: item.mileage,
    odometerReading: (item as ElasticVehicle | Item)?.odometerReading,
    gearboxType: item.gearboxType,
    horsepower: item.enginePower || null,
    segment: (item.properties || { segment: null }).segment,
    branches,
    branchDetails,
    financialOptions:
      ((item as ElasticVehicle | Item)?.financialOptions &&
        (item as ElasticVehicle | Item)?.financialOptions.length > 0 &&
        ((item as ElasticVehicle | Item)?.financialOptions || []).map(
          (f) => f.financialInstitution,
        )) ||
      null,
    insuranceOptions:
      ((item as ElasticVehicle | Item)?.insuranceOptions &&
        (item as ElasticVehicle | Item)?.insuranceOptions.length > 0 && [
          (item as ElasticVehicle | Item)?.insuranceOptions[0].institute,
        ]) ||
      null,
    sellerType:
      (item as ElasticVehicle | Item)?.sellerType ||
      ((item as ElasticVehicle | Item)?.branches && 'dealer') ||
      'private',
  };
};

const mapRetailerDetails = (item: Retailer | null) => {
  return {
    branchDetails: [
      {
        id: item && item.id,
        city: item && item.position && item.position.city,
        latitude: item && item.position && item.position.location && item.position.location.lat,
        longitude: item && item.position && item.position.location && item.position.location.lon,
        name: item && item.name,
        slug: item && item.slug,
      },
    ],
    branches: item?.id ? [item.id] : [],
  };
};

interface EventV2Data {
  eventType: any;
  searchData?: any;
  item?: any;
  params?: any;
  _pageType?: any;
  conversionType?: string | null;
  information?: any;
  list?: any;
  newsItemId?: any;
  newsItemName?: any;
  newsItemSearchMatchedId?: any;
  spreadable?: { [key: string]: any };
}

const eventV2 = (data: EventV2Data) => (_dispatch: Dispatch, getState: () => RootState) =>
  trackerHub(
    trackWithUser(getState(), {
      eventType: data.eventType,
      source,
      ...createConversionType(data.conversionType || null),
      url: location.href,
      ...createUrlParams(data.params || null),
      eventTimestamp: formatISO(new Date()),
      list: data.list || null,
      item: data.item,
      searchData: data.searchData,
      information: data.information || null,
      newsItemId: data.newsItemId || null,
      newsItemName: data.newsItemName || null,
      newsItemSearchMatchedId: data.newsItemId,
      pageType: EventContext.pageType,
      ...(data.spreadable || {}),
    }),
  );

const event =
  (
    eventType: string,
    searchData: any,
    item: any,
    params: any = null,
    _pageType: any = null,
    conversionType: string | null = null,
    information: any = null,
    list: any = null,
    newsItemId: any = null,
    newsItemName: any = null,
    newsItemSearchMatchedId: any = null,
    spreadable: any = null,
  ) =>
  (_dispatch: Dispatch, getState: () => RootState) =>
    trackerHub(
      trackWithUser(getState(), {
        eventType,
        source,
        ...createConversionType(conversionType),
        url: location.href,
        ...createUrlParams(params),
        eventTimestamp: formatISO(new Date()),
        list,
        item,
        searchData,
        information,
        newsItemId,
        newsItemName,
        newsItemSearchMatchedId,
        pageType: EventContext.pageType,
        ...spreadable,
      }),
    );

const eventInstant =
  (
    eventType: string,
    searchData: any,
    item: any,
    params: any = null,
    _pageType: any = null,
    conversionType: string | null = null,
    information: any = null,
    list: any = null,
    newsItemId: any = null,
    newsItemName: any = null,
    newsItemSearchMatchedId: any = null,
    spreadable: any = null,
  ) =>
  (_dispatch: Dispatch, getState: () => RootState) =>
    trackerHubInstant(
      trackWithUser(getState(), {
        eventType,
        source,
        ...createConversionType(conversionType),
        url: location.href,
        ...createUrlParams(params),
        eventTimestamp: formatISO(new Date()),
        list,
        item,
        searchData,
        information,
        newsItemId,
        newsItemName,
        newsItemSearchMatchedId,
        pageType: EventContext.pageType,
        ...spreadable,
      }),
    );

const ifCookieNotSet = (cookieName: string, value: boolean) => (actionIfNotSet: () => void) => {
  const cookie = Cookies.get(cookieName) || false;

  if (cookie) return;

  Cookies.set(cookieName, value, {
    path: '/',
  });

  actionIfNotSet();
};

/** START WD-2428 **/

const trackLoginModule =
  (conversionType: string, interactionType: string) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.LOGIN_MODULE,
      null,
      null,
      null,
      null,
      conversionType,
      null,
      null,
      null,
      null,
      null,
      {
        interactionType,
      },
    )(dispatch, getState);

const trackLoginModuleInstant =
  (conversionType: string, interactionType: string) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    eventInstant(
      eventTypes.LOGIN_MODULE,
      null,
      null,
      null,
      null,
      conversionType,
      null,
      null,
      null,
      null,
      null,
      {
        interactionType,
      },
    )(dispatch, getState);

// Popup-rutan är synlig
const trackLoginModuleInViewport =
  (interactionType: string) => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModule(conversionEvents.LOGIN_MODULE_IN_VIEWPORT, interactionType)(
      dispatch,
      getState,
    );

export const trackLoginModuleInViewportSaveObject =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.SAVE_OBJECT)(dispatch, getState);

export const trackLoginModuleInViewportSaveObjectInSearchResult =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.SAVE_OBJECT_IN_SEARCH_RESULT)(
      dispatch,
      getState,
    );

export const trackLoginModuleInViewportMessageDealer =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.MESSAGE_DEALER)(dispatch, getState);

export const trackLoginModuleInViewportSaveSearchResult =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.SAVE_SEARCH_RESULT)(dispatch, getState);

export const trackLoginModuleInViewportSaveCategory =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.SAVE_CATEGORY)(dispatch, getState);

export const trackLoginModuleInViewportSellToDealer =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInViewport(interactionEventType.SELL_TO_DEALER)(dispatch, getState);

// Klick på Logga in i popup-rutan
const trackLoginModuleClickLogin =
  (interactionType: string) => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleInstant(conversionEvents.LOGIN_MODULE_CLICK_LOGIN, interactionType)(
      dispatch,
      getState,
    );

export const trackLoginModuleClickLoginSaveObject =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.SAVE_OBJECT)(dispatch, getState);

export const trackLoginModuleClickLoginSaveObjectInSearchResult =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.SAVE_OBJECT_IN_SEARCH_RESULT)(
      dispatch,
      getState,
    );

export const trackLoginModuleClickLoginMessageDealer =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.MESSAGE_DEALER)(dispatch, getState);

export const trackLoginModuleClickLoginSaveSearchResult =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.SAVE_SEARCH_RESULT)(dispatch, getState);

export const trackLoginModuleClickLoginSaveCategory =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.SAVE_CATEGORY)(dispatch, getState);

export const trackLoginModuleClickLoginSellToDealer =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackLoginModuleClickLogin(interactionEventType.SELL_TO_DEALER)(dispatch, getState);

// Sälj till handlare
const trackSellToDelaer =
  (conversionType: string, interactionType: string) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.SELL_TO_DEALER,
      null,
      null,
      null,
      null,
      conversionType,
      null,
      null,
      null,
      null,
      null,
      {
        interactionType,
      },
    )(dispatch, getState);

export const trackSellToDelaerStartApplication =
  () => (dispatch: Dispatch, getState: () => RootState) =>
    trackSellToDelaer(
      conversionEvents.SELL_TO_DEALER_START_APPLICATION,
      interactionEventType.START_APPLICATION,
    )(dispatch, getState);

/** END WD-2428 **/

export const trackPageViewEvent =
  (item?: any, spreadable = {}) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const spread = {
      ...spreadable,
      documentTitle: document.title,
    };

    return event(
      eventTypes.PAGE_VIEW,
      null,
      item ? mapVehicleData(item) : null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      spread,
    )(dispatch, getState);
  };

export const trackViewEvent =
  (item: Item, spreadable: any = {}) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.ITEM_VIEWED,
      null,
      mapVehicleData(item),
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      spreadable,
    )(dispatch, getState);

export const trackExposureEvent =
  (item: ElasticVehicle | null, params: string, index: number, list: string | undefined) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.ITEM_EXPOSED,
      null,
      mapVehicleData(item),
      params,
      null,
      null,
      { index },
      list,
    )(dispatch, getState);

export const trackConversionEvent =
  (item: Item | Retailer | null, conversionType: string, spreadable: any = {}) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.CONVERSION,
      null,
      conversionType === otherEvents.CONVERSION_MAIL_SENT_RETAILER ||
        conversionType === otherEvents.CONVERSION_PHONENUMBER_CALL_RETAILER
        ? mapRetailerDetails(item as Retailer)
        : mapVehicleData(item as Item, true),
      null,
      null,
      conversionType,
      null,
      null,
      null,
      null,
      null,
      spreadable,
    )(dispatch, getState);

export const trackSoftConversionEvent =
  (item: Item | ElasticVehicle | null, conversionType: string, information: any = null) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const shouldTrackShareEvent =
      conversionType === softConversionEvents.SOFT_CONVERSIONS_SHARE &&
      !__softConversion.shareTracked.includes(information?.target);

    if (item && item?.id !== __softConversion.id) {
      __softConversion.id = item.id;
      __softConversion.tracked = [];
      event(eventTypes.SHOWN_INTEREST, null, mapVehicleData(item), null)(dispatch, getState);
    }

    if (!__softConversion.tracked.includes(conversionType) || shouldTrackShareEvent) {
      event(
        eventTypes.SOFT_CONVERSION,
        null,
        mapVehicleData(item),
        null,
        null,
        conversionType,
        information,
      )(dispatch, getState);
      __softConversion.tracked.push(conversionType);

      if (shouldTrackShareEvent) {
        __softConversion.shareTracked.push(information.target);
      }
    }
  };

export const trackSoftConversionEventInsurance =
  (
    item: Item | null,
    provider: InsuranceProviderResponse,
    conversionType: string,
    information: any = null,
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const shouldTrackShareEvent =
      conversionType === softConversionEvents.SOFT_CONVERSIONS_SHARE &&
      !__softConversion.shareTracked.includes(information?.target);

    if (!__softConversion.tracked.includes(conversionType) || shouldTrackShareEvent) {
      const vehicleData = mapVehicleData(item);
      vehicleData!.insuranceOptions = {
        institute: provider.institute,
        identifier: provider.identifier,
      } as unknown as string[];
      event(
        eventTypes.SOFT_CONVERSION,
        null,
        vehicleData,
        null,
        null,
        conversionType,
        information,
      )(dispatch, getState);
      __softConversion.tracked.push(conversionType);

      if (shouldTrackShareEvent) {
        __softConversion.shareTracked.push(information.target);
      }
    }
  };

type ShownInterestInsuranceType = {
  id: string | null;
};

const __shownInterestInsurance: ShownInterestInsuranceType = {
  id: null,
};

export const trackShownInterestInsurance =
  (item: Item | null, provider: InsuranceProviderResponse) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const vehicleData = mapVehicleData(item);

    vehicleData!.insuranceOptions = {
      institute: provider.institute,
      identifier: provider.identifier,
    } as unknown as string[];
    if (item && item.id !== __shownInterestInsurance.id && item.id !== __softConversion.id) {
      __shownInterestInsurance.id = item.id;
      event(eventTypes.SHOWN_INTEREST, null, vehicleData, null)(dispatch, getState);
    }
  };

export const beginSearchEvent = (params: string) => createSearchTimingObject(params);

export const trackSearchEvent =
  (params: string, searchResponse: ElasticSearchResponse<ElasticVehicle>) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.SEARCH,
      createSearchData(params, searchResponse),
      null,
      params,
    )(dispatch, getState);

export const trackRoutingEvent =
  (params: { path: string | undefined; from: string | undefined }) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(eventTypes.NAVIGATION, null, null, null, null, null, params)(dispatch, getState);

export const trackAddSubscription =
  (subscription: SubscriptionSearch, searchResults: RootState['search'] | undefined) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(eventTypes.SUBSCRIBED, null, null, null, null, null, {
      subscription,
      numberOfHits: searchResults?.documentList?.numberOfHits,
    })(dispatch, getState);

interface ExtendedAssignmentVehicle extends Vehicle {
  position?: {
    city?: string | null;
    location: {
      lan: string | number | null;
      lon: string | number | null;
    };
    zip?: string;
  };
}

export const trackAssignmentEvent =
  (eventType: string, data: ExtendedAssignmentVehicle, spreadable?: any) =>
  (_dispatch: Dispatch, getState: () => RootState) =>
    eventV2({
      eventType,
      information: data,
      spreadable,
    })(_dispatch, getState);

export const trackClickItemEvent =
  (item: ElasticVehicle, index: number, list: string | undefined) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.PRODUCT_CLICK,
      null,
      mapVehicleData(item),
      null,
      null,
      null,
      { index },
      list,
    )(dispatch, getState);

export const trackRegistrationEvent = () => (dispatch: Dispatch, getState: () => RootState) =>
  ifCookieNotSet(
    eventTypes.HAS_REGISTERED,
    true,
  )(() => event('registration', null, null, null, null, null, null)(dispatch, getState));

export const trackOtherEvent =
  (retailer: Retailer | null, eventType: string) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    return event(
      eventTypes.OTHER,
      null,
      null,
      null,
      null,
      eventType,
      null,
      null,
      null,
      null,
      null,
      {
        branch: {
          id: retailer?.id,
        },
      },
    )(dispatch, getState);
  };

export const trackSafetyAddons =
  (
    eventType: string,
    item: Item,
    data: { safetyAddon: { origin: 'conversation' } } | null = null,
  ) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(eventType, null, { ...mapVehicleData(item, true), ...data }, null)(dispatch, getState);

export const trackCreditApplicationEvent =
  (eventType: string, item: Item | null, data: any) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(eventType, null, { ...mapVehicleData(item, true), ...data }, null)(dispatch, getState);

interface TrackDefaultEventInformation {
  callToActionTitle?: string;
  callToActionUrl?: string;
}

export const trackDefaultEvent =
  (eventType: string, information: TrackDefaultEventInformation) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(eventType, null, null, null, null, null, information)(dispatch, getState);

export const trackEcomEvent =
  (eventType: string, eventInformation: string | null, item: Item | null) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventType,
      null,
      mapVehicleData(item),
      null,
      null,
      null,
      eventInformation,
    )(dispatch, getState);

export const trackPropertyTabFirstClick =
  (item: DetailedVehicle | Item | null | undefined, tab: PropertyTab) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    event(
      eventTypes.CAR_DETAIL_NAVIGATION,
      null,
      mapVehicleData(item),
      null,
      null,
      softConversionEvents.SOFT_CONVERSIONS_TAB_CLICK,
      tab,
    )(dispatch, getState);
    event(
      eventTypes.SHOWN_INTEREST,
      null,
      mapVehicleData(item),
      null,
      null,
      softConversionEvents.SOFT_CONVERSIONS_ITEM_PROPERTIES_TAB,
    )(dispatch, getState);
  };

export const trackPropertyTabClick =
  (item: Item | DetailedVehicle | null | undefined, tab: PropertyTab) =>
  (dispatch: Dispatch, getState: () => RootState) =>
    event(
      eventTypes.SOFT_CONVERSION,
      null,
      mapVehicleData(item),
      null,
      null,
      softConversionEvents.SOFT_CONVERSION_SHOW_VEHICLE_INFORMATION,
      tab,
    )(dispatch, getState);

interface TrackData {
  searchData?: any | null;
  item?: any | null;
  params?: any | null;
  conversionType?: any | null;
  information?: any | null;
  list?: any | null;
  newsItemId?: any | null;
  newsItemName?: any | null;
  newsItemSearchMatchedId?: any | null;
  spreadable?: any | null;
}

export const track =
  (
    eventType: string,
    {
      searchData = null,
      item = null,
      params = null,
      conversionType = null,
      information = null,
      list = null,
      newsItemId = null,
      newsItemName = null,
      newsItemSearchMatchedId = null,
      spreadable = null,
    }: TrackData,
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    event(
      eventType,
      searchData,
      item,
      params,
      null,
      conversionType,
      information,
      list,
      newsItemId,
      newsItemName,
      newsItemSearchMatchedId,
      spreadable,
    )(dispatch, getState);
  };

interface TrackAssignmentRemoval {
  information: Assignment & {
    reason: string;
    timeUntilSold: string;
  };
}

export const trackAssignmentRemoval = (spreadable: TrackAssignmentRemoval) =>
  track(assignmentEvents.REMOVE, { spreadable });

interface TrackChargingCostParameterChange {
  item: Item;
  information: {
    parameter: string;
    distance: string;
    context: string;
    chargingCost?: number;
    electricityCost?: number;
  };
}

export const trackChargingCostParameterChange = (spreadable: TrackChargingCostParameterChange) =>
  track(ElectricCostEvents.CHARGING_COST_PARAMETER_CHANGE, {
    spreadable,
  });

interface TrackChargingCostCustomizeClick {
  item: Item;
  information: {
    label?: string;
    parameter?: string;
    distance: string;
    context: string;
    chargingCost?: number;
    electricityCost?: number;
  };
}

export const trackChargingCostCustomizeClick = (spreadable: TrackChargingCostCustomizeClick) =>
  track(ElectricCostEvents.CHARGING_COST_CUSTOMIZE_CLICK, {
    spreadable,
  });

interface TrackChargingCostDetailsClick {
  information: {
    distance: string;
    context: string;
    chargingCost: number | undefined;
  };
}

export const trackChargingCostDetailsClick = (spreadable: TrackChargingCostDetailsClick) =>
  track(ElectricCostEvents.CHARGING_COST_DETAILS_CLICK, {
    spreadable,
  });

export const trackPropertiesFilterSearch = (
  item: Item | DetailedVehicle,
  spreadable: { search: string },
) =>
  track(softConversionEvents.SOFT_CONVERSIONS_ITEM_PROPERTIES_TAB_FILTER_SEARCH, {
    item: mapVehicleData(item),
    spreadable,
  });

export const trackEquipmentFilterSearch = (item: Item, spreadable: { search: string }) =>
  track(softConversionEvents.SOFT_CONVERSIONS_ITEM_EQUIPMENT_FILTER_SEARCH, {
    item: mapVehicleData(item),
    spreadable,
  });
